import { useMutation } from '@tanstack/react-query';

import type { ScanValidatedCustomer } from '@dutchie-pos/requests/checkin/camera-id-scan';
import type { TypedIdCreateInput } from '@dutchie-pos/requests/checkin/typed-id-entry';
import { toTypedIdCreateFields } from '@dutchie-pos/requests/checkin/typed-id-entry';

import { post } from 'src/utils/helpers/http';
import { useBaseAPIParams } from 'src/utils/hooks/useBaseAPIParams';

const CHECK_IN_GUEST_URL = 'v2/guest/checkin_guest';
const CREATE_GUEST_URL = 'v2/guest/create';
const CHECK_DUPLICATE_GUEST_URL = 'v2/guest/check-duplicate';

type CheckInGuestPayload = {
  AcctId: number;
  MJStateIDNo?: string;
  Register?: number;
};

type CheckInGuestResult = {
  ScanId: number;
  ScanResult: number;
  ShipmentId: number;
};

type CreatedGuest = {
  Guest_id: number;
};

/** Date-only, as `v2/guest/create` expects it; a null date must be sent as an empty string. */
const dateOnly = (value: string | null | undefined): string => value?.slice(0, 10) ?? '';

// The create request the back office form sends, filled from a scan-validated customer. Only the fields a scan can
// produce are set; the rest keep profile defaults, editable in the back office.
export const toCreateGuestPayload = (customer: ScanValidatedCustomer) => ({
  status: 'Active',
  NamePrefix: customer.NamePrefix ?? '',
  FirstName: customer.FirstName ?? '',
  MiddleName: customer.MiddleName ?? '',
  LastName: customer.LastName ?? '',
  NameSuffix: customer.NameSuffix ?? '',
  DOB: dateOnly(customer.DateOfBirth),
  street: customer.Address1 ?? '',
  street2: customer.Address2 ?? '',
  city: customer.City ?? '',
  state: customer.State ?? '',
  postal_code: customer.PostalCode ?? '',
  country_code: 'US',
  MJStateIDNo: customer.MJStateIdNumber ?? '',
  MJStateIDStartDate: dateOnly(customer.MJStateIdStartDate),
  ExpirationDate: dateOnly(customer.MJStateIdExpirationDate),
  DriversLicenseId: customer.DriversLicenseNumber ?? '',
  DLExpirationDate: dateOnly(customer.DriversLicenseExpirationDate),
  CustomerTypeId: customer.IsMedical ? 1 : 2,
  PhoneNo: customer.PhoneNumber ?? '',
  email: customer.Email ?? '',
});

export const useCheckInGuestMutation = () => {
  const baseAPIParams = useBaseAPIParams();

  return useMutation({
    mutationKey: ['check-in-guest'],
    mutationFn: (payload: CheckInGuestPayload) =>
      post<CheckInGuestResult[]>(CHECK_IN_GUEST_URL, { ...baseAPIParams, ...payload }),
  });
};

export const useCreateGuestMutation = () => {
  const baseAPIParams = useBaseAPIParams();

  return useMutation({
    mutationKey: ['create-guest'],
    mutationFn: (customer: ScanValidatedCustomer) =>
      post<CreatedGuest[]>(CREATE_GUEST_URL, { ...baseAPIParams, ...toCreateGuestPayload(customer) }),
  });
};

/**
 * The profile a *typed* entry creates, and deliberately not `toCreateGuestPayload` with the typed
 * fields spread over it.
 *
 * That payload exists for a decoded scan and does two things a typed entry must not do. It hard-codes
 * `CustomerTypeId: customer.IsMedical ? 1 : 2` — ids a given location may never have configured, and
 * the whole point of resolving the type from the location's own list is not to send one of those. And
 * it emits *both* identification slots, so spreading over it would leave the opposite slot on the
 * wire as an empty string, claiming staff checked a licence number they were never shown.
 *
 * So the base is built here rather than stripped afterwards: the two constants this endpoint needs
 * that a typed entry cannot supply, and then exactly the fields the shared mapper produces. An
 * unresolved customer type and the unused identification slot are *absent keys*, not blanks.
 */
export const toTypedGuestCreateBody = (entry: TypedIdCreateInput) => ({
  status: 'Active',
  country_code: 'US',
  ...toTypedIdCreateFields(entry),
});

export type TypedGuestCreateBody = ReturnType<typeof toTypedGuestCreateBody>;

/**
 * A profile the location may already have for this person.
 *
 * Only what this screen reads: the id it would check in, a name to show, the MJ id the check-in
 * wants, the status that tells an archived record apart, and the photo presence the shared
 * predicate asks about — optional and nullable, because a backend that does not project it and a
 * profile whose presence is unknown are the same absent answer, and neither means "no photo".
 */
export type DuplicateGuestMatch = {
  Guest_id?: number | null;
  FullName?: string | null;
  FirstName?: string | null;
  LastName?: string | null;
  MJStateIDNo?: string | null;
  status?: string | null;
  HasIdPhoto?: boolean | null;
};

/**
 * Does this location already have a profile for the person staff just typed?
 *
 * Asked before every typed create, never after: a second profile carries its own purchase history
 * and compliance record, and a duplicate created at the counter is the expensive failure this whole
 * fallback exists to avoid. Takes the create body itself, so the fields matched on are exactly the
 * fields that would be written.
 */
export const useCheckDuplicateGuestMutation = () => {
  const baseAPIParams = useBaseAPIParams();

  return useMutation({
    mutationKey: ['check-duplicate-guest'],
    mutationFn: (body: TypedGuestCreateBody) =>
      post<DuplicateGuestMatch | null>(CHECK_DUPLICATE_GUEST_URL, { ...baseAPIParams, ...body }),
  });
};

/** The typed sibling of `useCreateGuestMutation`: same endpoint, a body a typed entry can honestly fill. */
export const useCreateTypedGuestMutation = () => {
  const baseAPIParams = useBaseAPIParams();

  return useMutation({
    mutationKey: ['create-typed-guest'],
    mutationFn: (body: TypedGuestCreateBody) => post<CreatedGuest[]>(CREATE_GUEST_URL, { ...baseAPIParams, ...body }),
  });
};
